import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pushDataLayerEvent } from '../../../analytics/dataLayer';
import { Heading, Logo, NamedLink, PartnerBadge } from '../../../components';
import { categorySearchString, visibleCategories } from '../../../components/CategoryMenu/helpers';

import { categoryImage } from '../../LandingPage/sections/SectionCategoryTiles/categoryImages';

import css from './OnboardingWelcome.module.css';

/** The dataLayer event pushed when the welcome shows, for a GTM trigger. */
export const WELCOME_SHOWN_EVENT = 'welcome_shown';

// How many category circles: Klær, Sko and Tilbehør, as on the homepage.
const CATEGORY_CIRCLES = 3;

// The two buttons, in order: the first is the primary one (Guro, 9 October).
// The category circles above them cover browsing.
const MEMBER_ACTIONS = [
  { key: 'list', primary: true, name: 'NewListingPage', labelId: 'OnboardingWelcome.list' },
  { key: 'howToBuy', primary: false, name: 'HowToBuyPage', labelId: 'OnboardingWelcome.howToBuy' },
];

// A listing page someone can buy from or ask about: /l/:slug/:id and the
// pages under it (checkout, offer), or /l/:id. Not /l/new or the edit wizard.
const ITEM_PATH = /^\/l\/(?!new(\/|$))[^/]+(\/[^/]+)?(\/(checkout|make-offer|request-quote))?\/?$/;

/**
 * Whether `from` (a path or a location object) is an item's page.
 *
 * @param {string|Object} from
 * @returns {boolean}
 */
export const isItemPath = from => {
  const pathname = typeof from === 'string' ? from.split(/[?#]/)[0] : from?.pathname || '';
  return ITEM_PATH.test(pathname);
};

const IconArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M13 8H3m4.5-4.5L3 8l4.5 4.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ fill: 'none' }}
    />
  </svg>
);

const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 3l10 10M13 3L3 13"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      style={{ fill: 'none' }}
    />
  </svg>
);

const IconEnvelope = () => (
  <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden="true">
    <g
      style={{ fill: 'none' }}
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="1.6" y="3.4" width="12.8" height="9.2" rx="1" />
      <path d="m2 4 6 4.6L14 4" />
    </g>
  </svg>
);

/** Klær, Sko and Tilbehør with the homepage's photos and links. */
const CategoryCircles = ({ categories }) => (
  <ul className={css.circles}>
    {categories.map(category => {
      const image = categoryImage(category.id);
      return (
        <li key={category.id}>
          <NamedLink
            className={css.circleLink}
            name="SearchPage"
            to={{ search: categorySearchString([category.id]) }}
          >
            <span className={css.circle}>
              {image ? <img className={css.circleImage} src={image} alt="" /> : null}
            </span>
            <span className={css.circleLabel}>{category.name}</span>
          </NamedLink>
        </li>
      );
    })}
  </ul>
);

/**
 * The welcome right after signing up (/get-started): a dialog over the
 * homepage, so the new member is already inside the site.
 *
 * A member gets Guro's version: the categories, "Lag din første annonse" and
 * "Slik kjøper du". A business account (a Zuprize Partner signing up from its
 * invite) gets the partner version in a narrower box (an inset card on
 * phones): her badge, its own heading and text, and one action, its first
 * item. The partner wording is a placeholder until Guro's arrives.
 *
 * Every way out goes back to where the visitor came from when they signed up
 * from a page (a listing they wanted to buy or ask about), otherwise to the
 * homepage: the ×, Escape and a click outside the dialog. With somewhere to go
 * back to, an arrow link at the top left says so: "Tilbake til varen" from an
 * item, "Tilbake" from any other page. Whether the welcome shows at all is
 * AuthenticationPage's call (once per user, stamped on the profile).
 *
 * When it shows, `welcome_shown` goes to the GTM dataLayer, with the user
 * type, so a tag (e.g. Meta CompleteRegistration) can fire on it.
 *
 * @param {Object} props
 * @param {boolean} [props.emailVerified] - false for a fresh email signup with
 *   a verification email waiting; social sign-ins arrive verified
 * @param {boolean} [props.isBusiness] - a business account: the seller's version
 * @param {string} [props.from] - where the visitor was when they signed up
 * @param {Function} props.onDismiss - leaves the welcome (to `from` or home)
 * @returns {JSX.Element}
 */
const OnboardingWelcome = props => {
  const { emailVerified = true, isBusiness = false, from = null, onDismiss } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const dialogRef = useRef(null);

  useEffect(() => {
    pushDataLayerEvent(WELCOME_SHOWN_EVENT, { user_type: isBusiness ? 'business' : 'member' });
    dialogRef.current?.focus();
    // The page behind stays where it is while the welcome is open.
    const html = typeof document !== 'undefined' ? document.documentElement : null;
    html?.classList.add(css.noScroll);
    return () => html?.classList.remove(css.noScroll);
  }, [isBusiness]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  const handleBackdropClick = event => {
    if (event.target === event.currentTarget) {
      onDismiss();
    }
  };

  const categories = visibleCategories(config.categoryConfiguration?.categories || []).slice(
    0,
    CATEGORY_CIRCLES
  );

  return (
    <div
      className={classNames(css.root, { [css.rootPartner]: isBusiness })}
      onClick={handleBackdropClick}
      data-testid="onboarding-welcome"
    >
      <div
        className={classNames(css.dialog, { [css.dialogPartner]: isBusiness })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-welcome-title"
        tabIndex="-1"
        ref={dialogRef}
      >
        {/* Signed up from a page (a listing they wanted to buy or ask
            about): the way straight back to it, top left, opposite the ×. */}
        {from ? (
          <Link className={css.backLink} to={from}>
            <IconArrowLeft />
            <FormattedMessage
              id={isItemPath(from) ? 'OnboardingWelcome.backToItem' : 'OnboardingWelcome.back'}
            />
          </Link>
        ) : null}

        <button
          type="button"
          className={css.close}
          onClick={onDismiss}
          aria-label={intl.formatMessage({ id: 'AuthenticationPage.onboardingClose' })}
        >
          <IconClose />
        </button>

        {isBusiness ? (
          <PartnerBadge className={css.partnerBadge} />
        ) : (
          <Logo className={css.logo} layout="desktop" />
        )}

        <Heading as="h1" rootClassName={css.title} id="onboarding-welcome-title">
          <FormattedMessage
            id={
              isBusiness ? 'OnboardingWelcome.partnerTitle' : 'AuthenticationPage.onboardingTitle'
            }
          />
        </Heading>

        {isBusiness ? (
          <>
            <p className={css.body}>
              <FormattedMessage id="OnboardingWelcome.partnerBody" />
            </p>
            <div className={css.actions}>
              <NamedLink className={css.primaryAction} name="NewListingPage">
                <FormattedMessage id="OnboardingWelcome.partnerList" />
              </NamedLink>
            </div>
          </>
        ) : (
          <>
            <p className={css.body}>
              <span className={css.bodyLead}>
                <FormattedMessage id="OnboardingWelcome.accountCreated" />
              </span>
              <br />
              <FormattedMessage id="OnboardingWelcome.whatYouCanDo" />
            </p>
            {categories.length > 0 ? <CategoryCircles categories={categories} /> : null}
            <div className={css.actions}>
              {MEMBER_ACTIONS.map(action => (
                <NamedLink
                  key={action.key}
                  className={action.primary ? css.primaryAction : css.secondaryAction}
                  name={action.name}
                >
                  <FormattedMessage id={action.labelId} />
                </NamedLink>
              ))}
            </div>
          </>
        )}

        {emailVerified ? null : (
          <div className={css.verifyPanel}>
            <span className={css.verifyIcon}>
              <IconEnvelope />
            </span>
            <p className={css.verifyText}>
              <span className={css.verifyLead}>
                <FormattedMessage id="AuthenticationPage.onboardingVerifyEmail" />
              </span>
              <br />
              {/* True: Sharetribe sends no notification (purchases, sales,
                  messages) to an unconfirmed address, nor afterwards. */}
              <FormattedMessage id="OnboardingWelcome.verifyWhy" />
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWelcome;
